(function () {
  'use strict';

  const SITE_KEY = '6Lc9flcsAAAAAOFRh9xgyQdqJl8nVi-XlSNiuYIj';
  const ACTION = 'checkout';
  const VERIFY_ENDPOINT = 'https://xeropicks-server.vercel.app/api/captcha';
  const ERROR_MESSAGE = 'We could not verify reCAPTCHA. Please try again.';
  const REQUEST_TIMEOUT_MS = 15000;

  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href*="/checkout"]'
  ].join(',');

  const nativeSubmit = HTMLFormElement.prototype.submit;
  const verifiedElements = new WeakSet();
  const verifiedForms = new WeakSet();
  const loadingStates = new WeakMap();

  let isVerifying = false;

  const debugEnabled = (function () {
    try {
      return new URLSearchParams(window.location.search).get('mpRecaptchaDebug') === '1';
    } catch (error) {
      return false;
    }
  })();

  function debugLog(message, detail) {
    if (!debugEnabled) return;

    if (typeof detail === 'undefined') {
      console.log('[Moonpicks reCAPTCHA] ' + message);
    } else {
      console.log('[Moonpicks reCAPTCHA] ' + message, detail);
    }
  }

  function isCartForm(form) {
    return Boolean(
      form &&
      form.matches &&
      form.matches('form[action="/cart"], form[action*="/cart"]')
    );
  }

  function findCheckoutElement(event) {
    if (event.target && event.target.closest) {
      const element = event.target.closest(CHECKOUT_SELECTOR);

      if (element) return element;
    }

    if (event.composedPath) {
      const path = event.composedPath();

      for (let index = 0; index < path.length; index += 1) {
        const element = path[index];

        if (element && element.matches && element.matches(CHECKOUT_SELECTOR)) {
          return element;
        }
      }
    }

    return null;
  }

  function getElementForm(element) {
    if (!element) return null;
    if (element.form && isCartForm(element.form)) return element.form;

    const form = element.closest && element.closest('form');
    return isCartForm(form) ? form : null;
  }

  function stopEvent(event) {
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function setButtonLoading(element, isLoading) {
    if (!element || !element.matches || !element.matches('button, input')) return;

    if (isLoading) {
      if (loadingStates.has(element)) return;

      loadingStates.set(element, {
        ariaBusy: element.getAttribute('aria-busy'),
        disabled: element.disabled,
        childNodes: element.tagName === 'BUTTON' ? Array.from(element.childNodes) : null,
        value: element.tagName === 'INPUT' ? element.value : null
      });

      if (element.tagName === 'INPUT') {
        element.value = 'Verifying...';
      } else {
        element.replaceChildren(document.createTextNode('Verifying...'));
      }

      element.disabled = true;
      element.setAttribute('aria-busy', 'true');
      return;
    }

    const state = loadingStates.get(element);
    if (!state) return;

    if (element.tagName === 'INPUT') {
      element.value = state.value;
    } else {
      element.replaceChildren.apply(element, state.childNodes);
    }

    element.disabled = state.disabled;

    if (state.ariaBusy === null) {
      element.removeAttribute('aria-busy');
    } else {
      element.setAttribute('aria-busy', state.ariaBusy);
    }

    loadingStates.delete(element);
  }

  function getCaptchaToken() {
    return new Promise(function (resolve, reject) {
      if (
        !window.grecaptcha ||
        !window.grecaptcha.enterprise ||
        typeof window.grecaptcha.enterprise.ready !== 'function' ||
        typeof window.grecaptcha.enterprise.execute !== 'function'
      ) {
        reject(new Error('grecaptcha.enterprise is unavailable'));
        return;
      }

      let settled = false;
      const timeout = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('reCAPTCHA timed out'));
      }, REQUEST_TIMEOUT_MS);

      function finish(callback, value) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        callback(value);
      }

      try {
        window.grecaptcha.enterprise.ready(function () {
          try {
            window.grecaptcha.enterprise
              .execute(SITE_KEY, { action: ACTION })
              .then(function (token) {
                if (!token) {
                  finish(reject, new Error('reCAPTCHA returned an empty token'));
                  return;
                }

                finish(resolve, token);
              })
              .catch(function (error) {
                finish(reject, error);
              });
          } catch (error) {
            finish(reject, error);
          }
        });
      } catch (error) {
        finish(reject, error);
      }
    });
  }

  async function verifyCaptchaWithBackend(token) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(VERIFY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: token }),
        signal: controller ? controller.signal : undefined
      });

      const data = await response.json().catch(function () {
        return null;
      });

      return response.ok && data && data.success === true;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function verifyCaptcha() {
    const token = await getCaptchaToken();
    return verifyCaptchaWithBackend(token);
  }

  function showFailure(element, error) {
    isVerifying = false;
    setButtonLoading(element, false);
    debugLog('captcha failed', error);
    window.alert(ERROR_MESSAGE);
  }

  function runVerifiedCheckout(options) {
    if (isVerifying) {
      debugLog('checkout attempt ignored while verification is running');
      return;
    }

    isVerifying = true;
    setButtonLoading(options.element, true);
    debugLog('checkout attempt intercepted', options.source);

    verifyCaptcha()
      .then(function (passed) {
        if (!passed) {
          showFailure(options.element, new Error('Captcha backend returned success false'));
          return;
        }

        debugLog('captcha passed');
        isVerifying = false;
        setButtonLoading(options.element, false);
        options.continueCheckout();
      })
      .catch(function (error) {
        showFailure(options.element, error);
      });
  }

  function allowNextNativeSubmit(form) {
    verifiedForms.add(form);

    window.setTimeout(function () {
      verifiedForms.delete(form);
    }, 0);
  }

  function handleCheckoutClick(event) {
    const element = findCheckoutElement(event);
    if (!element) return;

    if (verifiedElements.has(element)) {
      verifiedElements.delete(element);
      debugLog('verified checkout click allowed');
      return;
    }

    stopEvent(event);

    runVerifiedCheckout({
      element: element,
      source: 'click',
      continueCheckout: function () {
        const form = getElementForm(element);

        verifiedElements.add(element);
        if (form) allowNextNativeSubmit(form);

        // Replaying the original click preserves AgeChecker and BSS/WPD handlers.
        element.click();

        window.setTimeout(function () {
          verifiedElements.delete(element);
        }, 0);
      }
    });
  }

  function handleCartSubmit(event) {
    const form = event.target;
    if (!isCartForm(form)) return;

    if (verifiedForms.has(form)) {
      verifiedForms.delete(form);
      debugLog('verified cart submit allowed');
      return;
    }

    stopEvent(event);

    const originalSubmitter = event.submitter;
    const submitter = originalSubmitter || form.querySelector('[name="checkout"]');

    runVerifiedCheckout({
      element: submitter,
      source: 'form submit',
      continueCheckout: function () {
        allowNextNativeSubmit(form);

        if (typeof form.requestSubmit === 'function') {
          if (originalSubmitter) {
            form.requestSubmit(originalSubmitter);
          } else {
            form.requestSubmit();
          }
        } else {
          verifiedForms.delete(form);
          nativeSubmit.call(form);
        }
      }
    });
  }

  HTMLFormElement.prototype.submit = function () {
    const form = this;

    if (!isCartForm(form)) {
      return nativeSubmit.call(form);
    }

    if (verifiedForms.has(form)) {
      verifiedForms.delete(form);
      return nativeSubmit.call(form);
    }

    debugLog('native form.submit intercepted');

    runVerifiedCheckout({
      element: form.querySelector('[name="checkout"]'),
      source: 'native form.submit',
      continueCheckout: function () {
        verifiedForms.add(form);

        try {
          nativeSubmit.call(form);
        } finally {
          verifiedForms.delete(form);
        }
      }
    });
  };

  window.addEventListener('click', handleCheckoutClick, true);
  window.addEventListener('submit', handleCartSubmit, true);
})();
